import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {ProvisionRunDto} from '../../../model/dto/entity/provision-run.dto';

/**
 * Provision runs table (route `cp/dashboard/runs`, hash routing `/#/cp/dashboard/runs`) —
 * 04-frontend.md §3.
 *
 * Built 1:1 following the documented bm original `features/feature-bm/bm/component/protected-components/
 * bm-billing-runs-component/bm-billing-runs.component.ts` (PAT §J.2): ONE QL table widget via
 * {@link WidgetFactory#createWidgetTableQl}, a row click navigates into the run detail. No
 * hand-built table HTML — the columns, filters, and paging are delivered by the widget framework.
 *
 * QL definition per 04 §3: start `cp.ProvisionRun` as `e`, LEFT JOIN on `provisionType` as `pt`,
 * parameter `resolve_fkey_texts=true` (so the FK columns show plain text instead of ids) and
 * sorting `createdDate desc` — the most recent run sits on top.
 *
 * Auto-refresh (04 §4): the component subscribes to `status$` of the job indicator that sits
 * globally in the top bar and rebuilds the widget on the running -> not-running transition. A new
 * WidgetData object is the repo-established programmatic reload — `MvsWidgetComponent`
 * reacts in `ngOnChanges` to a change of `widgetData` with `refreshComponent(false)`, i.e.
 * a real reload including layout. The indicator itself is neither built nor
 * activated here; that is done by the shell CpDashboardPage.
 */
@Component({
    selector: 'cp-provision-runs',
    templateUrl: './cp-provision-runs.component.html',
    styleUrls: ['./cp-provision-runs.component.scss'],
    standalone: false
})
export class CpProvisionRunsComponent implements OnInit, OnChanges, OnDestroy {

    /** Widget alias of the runs table (04 §3). */
    static readonly WIDGET_ALIAS: string = 'cp.dashboard.runs.table';

    busy: boolean;
    initialized: boolean;

    provisionRunsWidget: WidgetData;

    jobRunning: boolean;

    /** Visibility of the "Neuen Provisionslauf anlegen" dialog. */
    createDialogVisible: boolean = false;

    protected jobWasRunning: boolean;
    protected statusSubscription: Subscription;

    constructor(
        protected springBatchIndicatorService: JbSpringBatchIndicatorService,
        protected router: Router) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {
        this.refreshWidget();
        this.subscribeToJobCompletion();
        this.initialized = true;
    }

    /**
     * (Re-)build the runs table widget. Called on init and after a cp job has finished — a run that
     * was IN_CALCULATION when the page opened has a different status afterwards, and the table is
     * the surface on which that is supposed to become visible.
     */
    refreshWidget(): void {
        const qlQuery: QlQueryDto = {
            name: 'provisionRuns',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cp.ProvisionRun',
                as: 'e',
                joins: [
                    {
                        type: 'entity',
                        name: 'provisionType',
                        as: 'pt',
                        joinType: 'left'
                    }
                ],
                parameters: [
                    {param: 'resolve_fkey_texts', value: 'true'}
                ]
            },
            sortings: [
                new Sorting('e.createdDate', false)
            ]
        };

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: The one worklist of the "Provisionsläufe" nav item - cp.ProvisionRun rows with the
         *         provision type resolved to plain text (join provisionType, resolve_fkey_texts),
         *         newest run first. A row click opens cp/dashboard/run/:provisionRunId.
         */
        this.provisionRunsWidget = WidgetFactory.createWidgetTableQl(
            CpProvisionRunsComponent.WIDGET_ALIAS,
            'Provisionsläufe',
            qlQuery
        );
    }

    /**
     * Subscribe to the job indicator and reload on the running -> not-running transition (04 §4).
     */
    protected subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshWidget();
            }
        });
    }

    /**
     * Row click opens the run detail `cp/dashboard/run/:provisionRunId`.
     *
     * Absolute path on purpose: `run` is a SIBLING of `runs` under the dashboard shell (unlike bm,
     * where the detail is nested under the list route), so a relative navigation would have to walk
     * up first and would silently break if the route topology were ever nested.
     *
     * Deliberately NO `navItem` query param: CpDashboardPage.subscribeToParams reads that param and
     * navigates to the matching nav item, which would throw the user straight back out of the
     * detail. The shell keeps "Provisionsläufe" active for the `run` child on its own
     * (updateActiveNavigationFromRoute maps childPath 'run' -> nav item 'runs').
     */
    handleObjectSelect(object: ObjectIdentifierData): void {
        if (!object?.objectId) {
            return;
        }
        this.router.navigate(['/cp/dashboard/run', object.objectId]);
    }

    /** Button "Neuer Provisionslauf" — opens the create dialog. */
    openCreateDialog(): void {
        this.createDialogVisible = true;
    }

    /**
     * A run was created: refresh the list (it stays correct when the user comes back) and open the
     * new run, where its first step "Starte Berechnung" is waiting.
     */
    handleProvisionRunCreated(provisionRun: ProvisionRunDto): void {

        this.refreshWidget();

        if (provisionRun?.id) {
            this.router.navigate(['/cp/dashboard/run', provisionRun.id]);
        }
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.statusSubscription?.unsubscribe();
    }

}
